import type { ReactElement } from 'react';
import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { axisBottom, axisLeft, scaleBand, scaleLinear, select, symbol, symbolTriangle } from 'd3';
import type { NumberValue } from 'd3';
import { merge } from 'lodash';
import { max, min } from 'mathjs';
import { LadderNode } from 'feature-components/chart-parts/ladder/ladder';
import type { LadderSection } from 'helpers/ladder.helpers';
import Box from './box';
import BoxWhiskerTooltip from './tooltip';
import type { BoxWhiskerData, Config } from './types';
import XAxisLabel from './x-axis-label';
import './index.scss';

export type BoxWhiskerChartProps = {
  data: BoxWhiskerData[];
  numberOfBoxWhiskersAllowed?: number; // The number of box and whiskers that could fit within the chart. Used for spacing purposes.
  threshold?: number;
  referenceData?: BoxWhiskerData[];
  ladders?: LadderSection[][];
  config?: RecursivePartial<Config>;
  yLabel?: string;
  xLabel?: string;
  scaleUnit?: string;
  className?: string;
};

export const defaultConfig: Config = {
  margin: { default: 50, bottom: 60 },
  scale: { paddingRange: 10 },
  maxBoxWidth: 130,
  ladder: { width: 20, margin: 10 },
  width: 900,
  height: 550,
  animationDuration: 500,
  xAxis: { label: { writingMode: 'horizontal-tb' } }
};

const getPlotAreaTop = ({ margin }: Config): number => margin.top || margin.default;
const getPlotAreaBottom = ({ height, margin }: Config): number =>
  height - (margin.bottom || margin.default);
const getPlotAreaLeft = ({ margin, ladder }: Config, ladders = 0): number =>
  (margin.left || margin.default) + ladders * (ladder.width + ladder.margin * 2);
const getPlotAreaRight = ({ width, margin }: Config): number =>
  width - (margin.right || margin.default);
const getPlotAreaHeight = ({ height, margin }: Config): number =>
  height - (margin.top || margin.default) - (margin.bottom || margin.default);

const getScaleTop = (
  data: BoxWhiskerChartProps['data'],
  referenceData: BoxWhiskerChartProps['referenceData'],
  ladders: BoxWhiskerChartProps['ladders'],
  config: Config
): NumberValue =>
  max(
    ...([
      config.scale.maxLimit,
      max(
        ...([
          ...data.map(({ maximum }) => maximum),
          ...(referenceData?.map(({ maximum }) => maximum) || []),
          ...(ladders?.flat().map((ladder) => ladder.upperBound) || [])
        ].filter((v) => v !== undefined) as number[])
      ) + config.scale.paddingRange
    ].filter((v) => v !== undefined) as number[])
  );

const getScaleBottom = (
  data: BoxWhiskerChartProps['data'],
  referenceData: BoxWhiskerChartProps['referenceData'],
  ladders: BoxWhiskerChartProps['ladders'],
  config: Config
): NumberValue =>
  min(
    ...([
      config.scale.minLimit,
      min(
        ...([
          ...data.map(({ minimum }) => minimum),
          ...(referenceData?.map(({ minimum }) => minimum) || []),
          ...(ladders?.flat().map(({ lowerBound }) => lowerBound) || [])
        ].filter((v) => v !== undefined) as number[])
      ) - config.scale.paddingRange
    ].filter((v) => v !== undefined) as number[])
  );

/**
 * Can customise y & x axis labels, and the scale unit of the y axis,
 * as well as the following features:
 *
 * Area based themes:
 *  - different coloured boxes depending on area
 *
 * Tooltips:
 *  - header is boxWhisker label
 *  - displays same data points as plotted
 *
 * Ladders:
 *  - intended to display stanine/stage ladders
 *  - highlights relevant ladder when boxWhisker is hovered or clicked
 *
 */
export default function BoxWhiskerChart({
  data = [],
  numberOfBoxWhiskersAllowed = data.length,
  referenceData = [],
  ladders = [],
  threshold = 0,
  className,
  ...props
}: BoxWhiskerChartProps): ReactElement {
  // combine default and provided config
  const config = useMemo(() => {
    // We need to deep copy the default config since Lodash's merge mutates it:
    const defaultConfigCopy = structuredClone(defaultConfig);
    return merge(defaultConfigCopy, props.config);
  }, [props.config]);

  // calculate box width
  const boxWidth = Math.min(
    (getPlotAreaRight(config) - getPlotAreaLeft(config, ladders.length)) /
      numberOfBoxWhiskersAllowed,
    config.maxBoxWidth
  );

  /* setup axes */
  const xAxis = useMemo(
    () =>
      scaleBand<number | string>()
        .domain(data.map(({ id }) => id))
        .range([0, data.length * boxWidth])
        .padding(0.5),
    [boxWidth, data]
  );

  const yAxis = useMemo(
    () =>
      scaleLinear()
        .domain([
          getScaleBottom(data, referenceData, ladders, config),
          getScaleTop(data, referenceData, ladders, config)
        ])
        .range([getPlotAreaHeight(config), 0]),
    [config, data, ladders, referenceData]
  );

  /* track hover and selected states */
  const [selectedDataPoints, setSelectedDataPoints] = useState<number[]>([]);
  const [hoverDataPoints, setHoverDataPoints] = useState<number[]>([]);

  return (
    <svg
      className={clsx('box-whisker-chart', className)}
      preserveAspectRatio="xMidYMin meet"
      viewBox={`0 0 ${config.width} ${config.height}`}
      style={{ width: config.width, maxWidth: config.width }}
    >
      <g
        className="x-axis"
        transform={`translate(${getPlotAreaLeft(config, ladders.length)}, ${getPlotAreaBottom(
          config
        )})`}
      >
        <g
          className="ticks"
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          ref={(node) => select(node).call(axisBottom(xAxis).tickFormat(() => ''))}
        />
        <g
          className="grid"
          transform={`translate(
            ${-xAxis.bandwidth() / 2 - xAxis.padding() * xAxis.bandwidth()},
            ${config.margin.bottom || config.margin.default}
          )`}
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          ref={(node) => {
            const ticks = select(node).call(
              axisBottom(xAxis)
                .tickSize(
                  -getPlotAreaHeight(config) - config.margin.bottom || config.margin.default
                )
                .tickFormat(() => '')
            );
            ticks
              .selectAll('.tick')
              .attr('class', (d, i) => `tick ${data[i].className?.toLowerCase()}`);
            ticks.select('.domain').remove();
          }}
        />
        {data.map(({ label, id }) => (
          <XAxisLabel
            key={id}
            id={id}
            label={label}
            xAxis={xAxis}
            config={config}
            totalLabels={data.length}
          />
        ))}
        <text
          className="axis-label"
          textAnchor="end"
          y={(config.margin.bottom || config.margin.default) - 12}
          x={xAxis.range()[1]}
        >
          {props.xLabel}
        </text>
      </g>
      <g
        className="y-axis"
        transform={`translate(${getPlotAreaLeft(config, ladders.length)}, ${
          config.margin.top || config.margin.default
        })`}
      >
        {/* eslint-disable-next-line @typescript-eslint/explicit-function-return-type */}
        <g className="ticks" ref={(node) => select(node).call(axisLeft(yAxis))} />
        <path className="arrow-head" d={symbol().type(symbolTriangle)()} />
        <text
          className="axis-label"
          textAnchor="middle"
          fontSize={12}
          dy={props.scaleUnit ? -18 : -8}
          x={0}
        >
          {props.yLabel}
        </text>
        {!!props.scaleUnit && (
          <text className="axis-unit" textAnchor="middle" fontSize={12} dy={-8} x={0}>
            ({props.scaleUnit})
          </text>
        )}
      </g>
      <g
        className="ladders"
        transform={`translate(${config.ladder.margin},
          ${config.margin.top || config.margin.default})`}
      >
        {ladders?.map((l, i) => (
          <g
            key={l[0].id}
            transform={`translate(${i * (config.ladder.width + config.ladder.margin * 2)},0)`}
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            ref={(node) =>
              select(node)
                .selectAll('g')
                .data([l])
                .join('g')
                .append(() =>
                  LadderNode({
                    ladderTitle: l[0].name,
                    ladderData: l,
                    width: config.ladder.width,
                    margin: { left: config.ladder.margin, right: config.ladder.margin },
                    scales: { y: yAxis },
                    inactive:
                      ![...selectedDataPoints, ...hoverDataPoints].includes(l[0].id) &&
                      ladders?.length > 1
                  })
                )
            }
          />
        ))}
      </g>
      <g
        className="plot-area"
        transform={`translate(${getPlotAreaLeft(config, ladders.length)}, ${getPlotAreaTop(
          config
        )})`}
      >
        <g className="reference-data" transform={`translate(${xAxis.bandwidth() / 3},0)`}>
          {referenceData?.map((d) => (
            <g key={d.id} className={clsx('box-whisker')}>
              <Box
                data={d}
                scaleY={yAxis}
                boxScaleX={xAxis(d.id)}
                bandwidth={xAxis.bandwidth()}
                animationDuration={config.animationDuration}
                fillType="solid"
              />
            </g>
          ))}
        </g>
        <g className="learner-data">
          {data.map((d) => {
            if ((d.numberOfResponses || 0) > threshold) {
              return (
                <g
                  key={d.id}
                  id={`${d.id}-responses`}
                  className={clsx('box-whisker')}
                  {...(d.ladderId
                    ? {
                        onClick: (): void => {
                          if (selectedDataPoints.includes(d.ladderId)) {
                            setSelectedDataPoints((p) => p.filter((id) => id !== d.ladderId));
                          } else {
                            setSelectedDataPoints((p) => [...p, d.ladderId]);
                          }
                        },
                        onMouseEnter: () => setHoverDataPoints((p) => [...p, d.ladderId]),
                        onMouseLeave: () =>
                          setHoverDataPoints((p) => p.filter((id) => id !== d.ladderId)),
                        onFocus: () => setHoverDataPoints((p) => [...p, d.ladderId]),
                        onBlur: () => setHoverDataPoints((p) => p.filter((id) => id !== d.ladderId))
                      }
                    : {})}
                >
                  <Box
                    data={d}
                    scaleY={yAxis}
                    boxScaleX={xAxis(d.id)}
                    bandwidth={xAxis.bandwidth()}
                    animationDuration={config.animationDuration}
                    fillType="solid"
                  />
                  <BoxWhiskerTooltip
                    id={`${d.id}-responses`}
                    data={d}
                    referenceData={referenceData?.find((r) => r.id === d.id)}
                  />
                </g>
              );
            }
            return null;
          })}
        </g>
      </g>
    </svg>
  );
}
